import './SearchPalette.css';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { announce } from '../../a11y/LiveRegion';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Kbd } from '../../components/Kbd';
import { Portrait } from '../../components/Portrait';
import { UfTile } from '../../components/UfTile';
import { t } from '../../copy/strings';
import { ufOf } from '../../format/places';
import type { RouteState } from '../../model/types';
import { navigate, openProfile, setSearchPaletteReady, toggleSearch } from '../../state/actions';
import { useConfig, useLayout, useRouteState, useStatics, useTimeline, useTvOn, useUi } from '../../state/hooks';
import { normalizeSearch } from './normalize';
import { SEARCH_DIALOG_ID } from './SearchButton';
import {
  buildSearchIndex,
  type SearchIndex,
  type SearchItem,
  type SearchTarget,
  searchQuery,
  searchSuggestions,
} from './searchIndex';

const INPUT_ID = 'search-input';
const LIST_ID = 'search-results';
const STATUS_ID = 'search-status';
const optionId = (i: number) => `search-opt-${i}`;
/** Contagem de resultados anunciada 500 ms depois da última tecla (§5.8 bloco 7, X3 de §15.1.4). */
export const SEARCH_ANNOUNCE_MS = 500;
const MAX_QUERY = 60;
const APP_ROOT_ID = 'app-root';

/**
 * Quadros de nova tentativa ao devolver o foco ao `SearchButton`. No WebKit, o `focus()` dado no mesmo instante em que o
 * campo focado fica oculto às vezes começa a troca e desiste: o campo perde o foco apontando o botão e o foco fica no
 * `body` (e2e AC-5.8-04, 10 em 200 sob carga, 17/10).
 */
export const RETURN_FOCUS_RETRY_FRAMES = 10;

/** Fechar sem escolher devolve o foco ao botão (§5.8); se não pegou e ninguém o pôs em outro lugar, tenta de novo. */
export function returnFocusToButton(left = RETURN_FOCUS_RETRY_FRAMES): void {
  const button = document.querySelector<HTMLElement>('[data-testid="search-button"]');
  if (!button) return;
  button.focus();
  if (document.activeElement === button || left <= 0) return;
  requestAnimationFrame(() => {
    const now = document.activeElement;
    if (now === null || now === document.body) returnFocusToButton(left - 1);
  });
}

/** A UF do foco (bônus e sugestões, §5.8.3–§5.8.4): a da rota de UF ou de município; nenhuma no Brasil e no Exterior. */
export function searchFocusUf(place: RouteState['place']): SearchItem['uf'] {
  return place.kind === 'uf' || place.kind === 'municipality' ? place.uf : null;
}

/**
 * Ação ao escolher (§5.8.5): navega e, quando há perfil, abre-o no `then` do `navigate`, com a rota nova aplicada. Na
 * busca de presidente fora do ao vivo (o site revendo a noite), sem modo Candidato no mapa (§8.6).
 */
export function chooseTarget(target: SearchTarget, live: boolean): void {
  switch (target.kind) {
    case 'uf':
      navigate({ place: { kind: 'uf', uf: target.uf } });
      return;
    case 'municipality':
      // em Governadores sem F80 o roteador canoniza para a UF do município (§5.2.3)
      navigate({ place: { kind: 'municipality', uf: target.uf, ibge: target.ibge, zone: null } });
      return;
    case 'abroad':
      navigate({ office: 'president', place: { kind: 'abroad' } });
      return;
    case 'candidate':
      if (target.office === 'president') {
        const n = target.number;
        navigate(
          live
            ? { office: 'president', place: { kind: 'br' }, mapMode: 'candidate', mapCandidate: n }
            : { office: 'president', place: { kind: 'br' } },
          // biome-ignore lint/suspicious/noThenProperty: `then` é a opção do navigate (passo 9 de §5.1), não uma promessa
          { then: () => openProfile(n, 'president', 'search') },
        );
      } else {
        const n = target.number;
        navigate(
          { office: 'governor', place: { kind: 'uf', uf: target.uf } },
          // biome-ignore lint/suspicious/noThenProperty: `then` é a opção do navigate (passo 9 de §5.1), não uma promessa
          { then: () => openProfile(n, 'governor', 'search') },
        );
      }
      return;
  }
}

function Badge({ item, photos }: { item: SearchItem; photos: boolean }) {
  const b = item.badge;
  if (b.type === 'portrait') {
    return <Portrait candidate={b.candidate} photo={photos ? b.candidate.photo : null} size={30} />;
  }
  return <UfTile code={b.type === 'globe' ? 'ZZ' : b.text} fill="plain" size="row" />;
}

/**
 * Paleta de busca (§5.8): procura no próprio navegador em UFs, municípios, Exterior e candidatos (presidente e
 * governadores do 2º turno), a partir dos estáticos. Fica sempre montada e oculta (`hidden`) para o toque no
 * `SearchButton` focar o campo na mesma tarefa (iOS, §5.8 bloco 5); não existe na TV.
 *
 * Aberta: `inert` no `#app-root`, rolagem travada, foco no campo; setas e Tab andam sem dar a volta, Enter abre, Esc fecha
 * (cascata do App, §5.6), `mousedown` no véu fecha. Fechar sem escolher devolve o foco ao `SearchButton`; escolher não
 * (o foco vai ao destino, §5.9). O índice é montado só na 1ª abertura (§5.8.1; sem a montagem em tempo ocioso, que
 * pesava no tempo de bloqueio: docs/DECISOES.md §29).
 */
export function SearchPalette() {
  const tv = useTvOn();
  const open = useUi().searchOpen;
  const statics = useStatics();
  const config = useConfig();
  const route = useRouteState();
  const { live } = useTimeline();
  const coarse = useLayout().pointerCoarse;
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [index, setIndex] = useState<SearchIndex | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chosen = useRef(false);

  useEffect(() => {
    setSearchPaletteReady(true);
    return () => setSearchPaletteReady(false);
  }, []);

  // §5.8.1: o índice uma vez, na 1ª abertura. Sem a montagem em tempo ocioso depois da carga: ela virava uma tarefa
  // longa logo depois do herói e o tempo de bloqueio do Lighthouse passou de 200 para 595 ms (docs/DECISOES.md §29)
  const geography = statics.geography;
  const candidates = statics.candidates;
  const flags = { abroad: config.features.abroad, governors: config.features.governors };
  /** A entrada do último índice: ele só é refeito quando a geografia, o cadastro ou as flags mudam (§5.8.1). */
  const builtFrom = useRef<unknown[] | null>(null);
  const inputs = [geography, candidates, flags.abroad, flags.governors];
  const build = () => {
    if (!geography) return;
    const prev = builtFrom.current;
    if (prev?.every((v, i) => v === inputs[i])) return;
    builtFrom.current = inputs;
    setIndex(buildSearchIndex(geography, candidates, flags));
  };
  useEffect(() => {
    if (!geography) return;
    if (open) {
      build();
      return;
    }
    if (index) build();
  }, [geography, candidates, open, flags.abroad, flags.governors]);

  const focusUf = searchFocusUf(route.place);
  // o passo 1 do §5.8.3: a consulta NORMALIZADA vazia (só pontuação, por exemplo) mostra as sugestões
  const suggest = normalizeSearch(query) === '';
  const results = useMemo(
    () => (index ? (suggest ? searchSuggestions(index, focusUf) : searchQuery(index, query, focusUf)) : []),
    [index, query, focusUf, suggest],
  );
  const groupLabel = suggest
    ? focusUf
      ? t('search.group.largestUf', { uf_de: ufOf(focusUf) })
      : t('search.group.largest')
    : null;

  // abrir: consulta vazia, 1º ativo, inert no app, rolagem travada, foco no campo; fechar: desfaz e devolve o foco
  useLayoutEffect(() => {
    if (!open || tv) return;
    chosen.current = false;
    setQuery('');
    setActive(0);
    const appRoot = document.getElementById(APP_ROOT_ID);
    const ownsInert = appRoot !== null && !appRoot.hasAttribute('inert');
    if (ownsInert) appRoot.setAttribute('inert', '');
    const html = document.documentElement;
    html.classList.add('search-open');
    inputRef.current?.focus({ preventScroll: true });
    return () => {
      if (ownsInert) appRoot.removeAttribute('inert');
      html.classList.remove('search-open');
      if (!chosen.current) returnFocusToButton();
    };
  }, [open, tv]);

  // a contagem pela região viva única, 500 ms depois da última tecla (nunca a cada tecla)
  useEffect(() => {
    if (!open || suggest || !index) return;
    const id = setTimeout(() => {
      const n = results.length;
      announce(
        n === 0
          ? t('search.status.empty', { q: query })
          : n === 1
            ? t('search.status.one')
            : t('search.status.count', { N: n }),
        'response',
      );
    }, SEARCH_ANNOUNCE_MS);
    return () => clearTimeout(id);
  }, [open, query, index]);

  // a lista encolheu (ou chegou depois de uma seta): o ativo volta para dentro dela
  useEffect(() => {
    if (active > 0 && active >= results.length) setActive(0);
  }, [results, active]);

  if (tv) return null;

  const choose = (item: SearchItem | undefined) => {
    if (!item) return;
    chosen.current = true;
    toggleSearch(false);
    chooseTarget(item.target, live);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const last = results.length - 1;
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setActive((a) => Math.max(0, Math.min(last, a + 1)));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    }
  };

  // "Carregando" até a geografia e o cadastro de candidatos chegarem (§5.8 bloco 4)
  const status =
    !index || !candidates
      ? t('search.loading')
      : !suggest && results.length === 0
        ? t('search.empty', { q: query })
        : null;
  const photos = config.features.photos;
  const placeholder = t('search.input.placeholder');

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: o véu só fecha no mousedown fora da caixa (§5.8 bloco 5); Esc e "Cancelar" são os caminhos de teclado e toque
    <div
      class="search-palette"
      data-testid="search-palette"
      hidden={!open}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) toggleSearch(false);
      }}
    >
      <div
        // um clique fora do campo, do "Cancelar" e das opções não tira o foco do campo (§5.8 bloco 7)
        onMouseDown={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('input, button, [role="option"]')) return;
          e.preventDefault();
        }}
        id={SEARCH_DIALOG_ID}
        class="search-palette__box"
        role="dialog"
        aria-modal="true"
        aria-label={t('search.dialog.aria')}
      >
        <div class="search-palette__field">
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            id={INPUT_ID}
            class="search-palette__input"
            data-testid="search-input"
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls={LIST_ID}
            aria-autocomplete="list"
            aria-activedescendant={results.length > 0 ? optionId(active) : undefined}
            aria-describedby={status ? STATUS_ID : undefined}
            aria-label={t('search.input.aria')}
            placeholder={placeholder}
            autocomplete="off"
            autocapitalize="off"
            spellcheck={false}
            enterkeyhint="go"
            maxLength={MAX_QUERY}
            value={query}
            onInput={(e) => {
              setQuery((e.currentTarget as HTMLInputElement).value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          {coarse ? (
            <Button
              class="search-palette__cancel"
              variant="ghost"
              label={t('search.cancel')}
              onClick={() => toggleSearch(false)}
            />
          ) : (
            <Kbd keys="esc" ariaHidden />
          )}
        </div>
        {groupLabel && results.length > 0 ? (
          <p class="search-palette__group" aria-hidden="true">
            {groupLabel}
          </p>
        ) : null}
        <div
          id={LIST_ID}
          class="search-palette__list"
          role="listbox"
          aria-label={groupLabel ?? t('search.results.aria')}
          data-testid="search-results"
        >
          {results.map((item, i) => (
            // biome-ignore lint/a11y/useFocusableInteractive: combobox com aria-activedescendant (§5.8 bloco 7): o foco fica no campo, nunca na opção
            // biome-ignore lint/a11y/useKeyWithClickEvents: as teclas são do campo (↑ ↓ Tab Enter, onKeyDown)
            <div
              key={item.id}
              id={optionId(i)}
              class="search-palette__option"
              role="option"
              aria-selected={i === active ? 'true' : 'false'}
              aria-label={`${item.label}, ${item.sub}`}
              data-id={item.id}
              onMouseMove={() => {
                if (i !== active) setActive(i);
              }}
              onClick={() => choose(item)}
            >
              <span class="search-palette__badge" aria-hidden="true">
                <Badge item={item} photos={photos} />
              </span>
              <span class="search-palette__text">
                <span class="search-palette__label">{item.label}</span>
                <span class="search-palette__sub">{item.sub}</span>
              </span>
            </div>
          ))}
        </div>
        {status ? (
          <p id={STATUS_ID} class="search-palette__status" data-testid="search-status">
            {status}
          </p>
        ) : null}
        {coarse ? null : (
          <p class="search-palette__footer" aria-hidden="true">
            {t('search.footer')}
          </p>
        )}
      </div>
    </div>
  );
}
